import type { GetMessagesQuery } from '#src/graphql/generated';

import { Alert, Box, Spinner, Text, VStack } from '@chakra-ui/react';

import {
  MessageCard,
  type ChatMessageQueryOptions,
  type MessageCardVariant,
} from '#src/components/message-card';

export type MessageListMessage = GetMessagesQuery['messages'][number];

export type { ChatMessageQueryOptions };

export interface MessageListProps {
  messages: MessageListMessage[];
  loading: boolean;
  error: Error | undefined;
  emptyMessage?: string;
  /** Resolved thread root when URL has `focusMessageId`; drives expanded vs collapsed UI. */
  focusedRootId: string | null;
  onExpandThread: (rootId: string) => void;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  onRunChatMessageQuery: (
    messageId: string,
    query: string,
    options?: ChatMessageQueryOptions,
  ) => Promise<void>;
  chatMessageQueryLoadingId: string | null;
  /** When true, messages sit at the bottom of the scroll area (e.g. above a fixed chat input). */
  alignMessagesToBottom?: boolean;
  /** Called after a message is soft-deleted so the parent can refetch the list. */
  onMessageDeleted?: () => void | Promise<void>;
}

export function MessageList({
  messages,
  loading,
  error,
  emptyMessage = 'No messages found.',
  focusedRootId,
  onExpandThread,
  messagesEndRef,
  onRunChatMessageQuery,
  chatMessageQueryLoadingId,
  alignMessagesToBottom = false,
  onMessageDeleted,
}: MessageListProps) {
  const variant: MessageCardVariant = focusedRootId ? 'expanded' : 'collapsed';

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text fontSize="sm" color="fg.muted" mt={4}>
          Loading messages...
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert.Root status="error">
        <Alert.Indicator />
        <Alert.Title>Error loading messages: {error.message}</Alert.Title>
      </Alert.Root>
    );
  }

  if (messages.length === 0) {
    if (alignMessagesToBottom) {
      return (
        <Box
          flex={1}
          minHeight={0}
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
          overflow="hidden"
        >
          <Text color="fg.muted" textAlign="center" py={4}>
            {emptyMessage}
          </Text>
        </Box>
      );
    }
    return (
      <Text color="fg.muted" textAlign="center" py={8}>
        {emptyMessage}
      </Text>
    );
  }

  return (
    <Box flex={1} minHeight={0} overflowY="auto">
      <VStack
        gap={3}
        align="stretch"
        minHeight={alignMessagesToBottom ? '100%' : undefined}
        justifyContent={alignMessagesToBottom ? 'flex-end' : undefined}
      >
        {messages.map((message) => (
          <MessageCard
            key={message.id}
            message={message}
            childMessages={message.childMessages}
            variant={variant}
            onExpandThread={
              variant === 'collapsed' ? onExpandThread : undefined
            }
            onRunChatMessageQuery={onRunChatMessageQuery}
            chatMessageQueryLoadingId={chatMessageQueryLoadingId}
            onMessageDeleted={onMessageDeleted}
          />
        ))}
        <Box ref={messagesEndRef} />
      </VStack>
    </Box>
  );
}
